import {DataFetcher, useLegacyGrpcRequest} from '@aboutyou/data-fetcher';
import {CheckoutV1_getCheckoutDetailsLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {useTranslation} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {Log} from '@aboutyou/mlm';
import {useBasketRoute, useCheckoutRoute, useHistory} from '@aboutyou/router';
import {useCOTracking} from '@aboutyou/tracking';
import {
  ErrorPageInline,
  HEADER_HEIGHT,
  Z_INDEXES,
  useShowToast,
} from '@aboutyou/ui-components';
import React, {useRef, useState} from 'react';
import styled from 'styled-components';
import {useRenderAsTwa} from '../../client-services/Twa/hooks/useRenderAsTwa';
import {getAppIntentUrlForDevices} from '../../features/AppRedirect/helpers';
import {
  CHECKOUT_URL_PARAMS,
  DEVICE_PARAM_TYPE,
} from '../../features/Checkout/constants';
import {
  ExpressCheckoutErrorEvent,
  ExpressCheckoutFinishedEvent,
  RawExpressCheckoutComponent,
} from '../Basket/ExpressCheckout/RawExpressCheckoutComponent';
import {CheckoutAnimation} from './components/CheckoutAnimation/CheckoutAnimation';
import {ForceAppRedirectListener} from './components/ForceAppRedirectListener';
import {useAppUrlParams} from './hooks/useAppUrlParams';

export const AppExpressCheckoutPaymentButtons = () => {
  const hydrationDone = useHydrationDone();

  const {oAuthHash, device} = useAppUrlParams();
  const isTwaEnabled = useRenderAsTwa();

  if (!hydrationDone) {
    return <CheckoutAnimation.Show />;
  }

  return (
    <>
      <ForceAppRedirectListener />

      {!isTwaEnabled && <NonClickableTransparentHeaderCover />}

      <CheckoutAccessTokenFetcher oAuthHash={oAuthHash!} device={device} />
    </>
  );
};

const CheckoutAccessTokenFetcher: React.FC<{
  oAuthHash: string;
  device: DEVICE_PARAM_TYPE;
}> = ({oAuthHash, device}) => {
  const checkoutRoute = useCheckoutRoute();
  const {t} = useTranslation();
  const history = useHistory();
  const basketRoute = useBasketRoute();
  const {track} = useCOTracking();
  const showToast = useShowToast();
  const webComponentRef = useRef<HTMLDivElement>(null);

  const navigateToCheckout = (searchParams: URLSearchParams) => {
    const entryTime = Date.now().toString();
    searchParams.delete(CHECKOUT_URL_PARAMS.clientEntryTimeStamp);
    searchParams.delete(CHECKOUT_URL_PARAMS.entryTimeStamp);

    searchParams.set(CHECKOUT_URL_PARAMS.entryTimeStamp, entryTime);
    searchParams.set(CHECKOUT_URL_PARAMS.clientEntryTimeStamp, entryTime);

    history.replace({
      pathname: checkoutRoute,
      search: `?${searchParams.toString()}`,
    });
  };

  const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);

  const handlePaymentStart = () => {
    /**
     * The earliest event we get from the web component 'payment-start' still doesn't render the widget UI
     * So we need to wait a bit longer to hide the spinner, thus the 2.5s timeout
     *
     * NOTE: The loading spinner will not block the webcomponent UI for the user since its rendered as a overlay above the spinner, thus its okay that the timeout tio hide the spinner is longer and not a perfect match
     */
    setTimeout(() => {
      setShowLoadingSpinner(false);
    }, 5000);
  };

  const hideLoadingSpinner = () => {
    setShowLoadingSpinner(false);
  };

  const request = useLegacyGrpcRequest(CheckoutV1_getCheckoutDetailsLazy, {
    hash: oAuthHash,
  });

  return (
    <DataFetcher
      request={request}
      View={props => {
        let parsedData: {
          expressCheckoutProvider: 'klarna' | 'paypal';
          basketTotal: number;
        };

        try {
          const paymentProvider = props.jwt.split('.');
          const data = atob(paymentProvider[1]!);
          const returnData = JSON.parse(data);
          parsedData = returnData.customData;
        } catch (e) {
          Log.error(
            '@aboutyou/logic-components',
            'ExpressCheckout Error parsing jwt data',
            {
              error: e,
            },
          );
          throw new Error(
            '[AppExpressCheckoutPaymentButtons] Error parsing jwt data',
          );
        }

        const handleInvalidAccessToken = () => {
          props.hideLoadingSpinner();
          Log.error(
            '@aboutyou/logic-components',
            'Invalid access token handle called on app',
          );
          const searchParams = new URLSearchParams(document.location.search);
          searchParams.set(CHECKOUT_URL_PARAMS.expressFallback, '1');
          searchParams.set(CHECKOUT_URL_PARAMS.authToken, props.authToken);
          searchParams.set(
            CHECKOUT_URL_PARAMS.authProvider,
            props.authProvider,
          );
          props.goToCheckout(searchParams);
        };

        const handlePaymentCancelled = () => {
          props.hideLoadingSpinner();
          track({
            type: 'ExpressCheckoutCancel',
            pc180Score: 0,
            expressPaymentKey: parsedData?.expressCheckoutProvider,
          });
          const redirectUrl = getAppIntentUrlForDevices({
            device,
            searchParams: '',
            intent: 'basket',
          });

          window.location.href = redirectUrl;
        };

        const handleExpressError = ({detail}: ExpressCheckoutErrorEvent) => {
          props.hideLoadingSpinner();
          track({
            type: 'ExpressCheckoutWidgetError',
            pc180Score: 0,
            expressPaymentKey: parsedData.expressCheckoutProvider,
            errorReason: typeof detail === 'string' ? detail : detail?.message,
            errorKey: typeof detail === 'string' ? detail : detail?.errorKey,
            statusCode: typeof detail === 'string' ? 0 : detail?.statusCode,
          });

          showToast({
            type: 'ERROR',
            content: t(
              'Oops! An error has occurred with the {provider} functionality. Please use the regular checkout',
              {
                provider:
                  parsedData.expressCheckoutProvider === 'klarna'
                    ? 'Klarna Express'
                    : 'PayPal Express',
              },
            ),
          });

          const searchParams = new URLSearchParams(document.location.search);
          searchParams.set(CHECKOUT_URL_PARAMS.expressFallback, '1');
          searchParams.set(CHECKOUT_URL_PARAMS.authToken, props.authToken);
          searchParams.set(
            CHECKOUT_URL_PARAMS.authProvider,
            props.authProvider,
          );
          props.goToCheckout(searchParams);
        };

        const handleCheckoutFinished = ({
          detail,
        }: ExpressCheckoutFinishedEvent) => {
          const searchParams = new URLSearchParams(document.location.search);
          searchParams.set(CHECKOUT_URL_PARAMS.transactionId, detail?.id);
          searchParams.set(
            CHECKOUT_URL_PARAMS.paymentProvider,
            parsedData.expressCheckoutProvider,
          );
          searchParams.set(CHECKOUT_URL_PARAMS.authToken, props.authToken);
          searchParams.set(
            CHECKOUT_URL_PARAMS.authProvider,
            props.authProvider,
          );
          track({
            type: 'ExpressCheckoutAuthorization',
            pc180Score: 0,
            expressPaymentKey: parsedData.expressCheckoutProvider,
            review: !detail.isFinalized, // True if the customer is delegated to the review page; False if the customer goes directly to OSP.
          });

          if (!detail.isFinalized) {
            props.goToCheckout(searchParams);
          } else {
            Log.error(
              '@aboutyou/logic-components',
              'isFinalized is true on express checkout',
            );
          }
        };

        return (
          <>
            {props.showLoadingSpinner ? (
              <CheckoutAnimation.Show />
            ) : (
              <CheckoutAnimation.End />
            )}

            <RawExpressCheckoutComponent
              ref={webComponentRef}
              data-testid={`express-checkout-web-component-${parsedData.expressCheckoutProvider}`}
              device={device}
              jwt={props.jwt}
              accessToken={props.accessToken}
              basketTotal={parsedData.basketTotal}
              // hardcoded for now, in future when additional provider are added in we would need to change this logic
              // added because CO doesnt like idea of just passing boolean value
              expressDirectEntry={{
                paymentKey: 'spay_klarna_paylater',
                provider: 'klarna',
              }}
              onInvalidAccessToken={handleInvalidAccessToken}
              onExpressCheckoutFinished={handleCheckoutFinished}
              onPaymentClosed={handlePaymentCancelled}
              onExpressError={handleExpressError}
              onPaymentStart={props.handlePaymentStart}
              hasDatadogConsent={false}
            />
          </>
        );
      }}
      viewProps={{
        goToCheckout: navigateToCheckout,
        showLoadingSpinner,
        hideLoadingSpinner,
        handlePaymentStart,
      }}
      Error={({error}) => {
        Log.error(
          '@aboutyou/logic-components',
          'CheckoutAccessTokenFetcher Error',
          {error: error as Error},
        );
        CheckoutAnimation.End();

        return (
          <ErrorViewWrapper>
            <ErrorPageInline url={basketRoute} />
          </ErrorViewWrapper>
        );
      }}
      Loading={CheckoutAnimation.Show}
    />
  );
};

/**
 * Prevents users from interacting ( clicking, long-pressing ) on the Logo
 * when Checkout page is shown in the App
 */
const NonClickableTransparentHeaderCover = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: ${HEADER_HEIGHT}px;
  width: 100vw;
  z-index: ${Z_INDEXES.MAIN_HEADER + 1};
`;

const ErrorViewWrapper = styled.div`
  margin-top: 4rem;
`;
